import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ROUTES from '@src/constants/route';
import actions from '@src/redux/actions';
import PackageInfoV3 from '@src/containers/Layout/SidebarNew/PackageInfoV3';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { Box, IconButton, Typography } from '@mui/material';
import {
  AddShoppingCartRounded,
  ArrowBackRounded as ArrowBackRoundedIcon,
} from '@mui/icons-material';
import { MAX_TITLE_LENGTH } from '@src/constants/project';
import { SMS_IDENTITY_PROVIDER } from '@src/constants';
import useElementWidth from '@src/hooks/useElementWidth';
import { countTextLengthBlocks } from '@src/services/project';
import ReferAndGetRewardVi from '@src/assets/images/refer-and-get-reward-vi.svg';
import ReferAndGetRewardEn from '@src/assets/images/refer-and-get-reward-en.svg';
import {
  StyledHeader,
  StyledProjectTitle,
  StyledSidebarTooltip,
  StyledTooltip,
} from './index.style';
import DownloadButton from './DownloadButton';
import SaveButton from './SaveButton';

const WIDTH_DIVIDER = 400;

const Header = () => {
  const titleRef = useRef();
  const titleWidth = useElementWidth(titleRef);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isVietnamese = i18n.language === 'vi';
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();

  const { title, multiBlock } = useSelector((state) => state.project);
  const blocks = multiBlock?.blocks;
  const user = useSelector((state) => state.auth.user);
  const { openSidebar } = useSelector((state) => state.layout);
  const { identityProvider } = user;
  const [newTitle, setNewTitle] = useState(title);
  const isSMSUser = useMemo(
    () => Object.values(SMS_IDENTITY_PROVIDER).includes(identityProvider),
    [identityProvider],
  );
  const useNewSidebarV2 = getFeatureValue(FEATURE_KEYS.NEW_SIDEBAR_V2, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const handleValidateProjectTitle = (value) => {
    if (value.length <= MAX_TITLE_LENGTH) return true;
    dispatch(
      actions.noti.push({
        severity: 'warning',
        message: 'updateTitleMaxLength',
        value: MAX_TITLE_LENGTH,
      }),
    );
    return false;
  };

  const handleChangeProjectTitle = (e) => {
    const validTitle = handleValidateProjectTitle(e.target.value);
    if (!validTitle) return;
    setNewTitle(e.target.value);
  };

  const handleNavigateToPayment = () => {
    history.push(ROUTES.PAYMENT);
  };

  const updateProjectTitle = async () => {
    if (!newTitle) {
      setNewTitle(title);
      return;
    }
    if (newTitle !== '' && newTitle !== title) {
      dispatch(actions.project.updateTitle(newTitle));
    }
  };
  const referralEnabled = getFeatureValue(FEATURE_KEYS.REFERRAL, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });
  const renderButtonReferAndGetReward = () =>
    referralEnabled ? (
      <IconButton
        size="small"
        color="secondary"
        onClick={() => {
          history.push(ROUTES.REFERRAL);
        }}
        className="icon-btn-tooltip-refer-and-get-reward"
      >
        <img
          src={isVietnamese ? ReferAndGetRewardVi : ReferAndGetRewardEn}
          alt="icon"
        />
      </IconButton>
    ) : (
      <></>
    );
  const userPackageInfo = () => (
    <>
      <div className="container-user-info">
        <div className="container-btn">
          <SaveButton />
          <DownloadButton />
        </div>

        <div className="divider" />
        {renderButtonReferAndGetReward()}
        <div className="wallet">
          <Box className="wallet-character">
            <PackageInfoV3
              isSMSUser={isSMSUser}
              useNewSidebarV2={useNewSidebarV2}
            />
            <StyledTooltip title={t('buyPackage')} placement="left">
              <div className="icon-container">
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={handleNavigateToPayment}
                  className="icon-btn-tooltip"
                >
                  <AddShoppingCartRounded />
                </IconButton>
              </div>
            </StyledTooltip>
          </Box>
        </div>
      </div>
    </>
  );

  const renderProjectTitle = () => (
    <StyledProjectTitle
      maxWidth={titleWidth - WIDTH_DIVIDER}
      openSidebar={openSidebar}
    >
      <div className="input-wrapper">
        <span className="size-span">{newTitle}</span>
        <input
          value={newTitle}
          placeholder={t('noTitle')}
          className="size-input"
          onChange={handleChangeProjectTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.target.blur();
            }
          }}
          onBlur={updateProjectTitle}
        />
      </div>
      <Typography className="createdAt">
        {countTextLengthBlocks(blocks).toLocaleString(language)}{' '}
        {t('characters').toLowerCase()}
      </Typography>
    </StyledProjectTitle>
  );

  const renderGoBack = () => (
    <Box className="go-back-container">
      <StyledSidebarTooltip title={t('goBack')}>
        <IconButton
          onClick={() => {
            history.push(ROUTES.PROJECTS);
          }}
        >
          <ArrowBackRoundedIcon className="go-back-icon" />
        </IconButton>
      </StyledSidebarTooltip>
    </Box>
  );
  return (
    <StyledHeader ref={titleRef}>
      <div className="container-header">
        {renderGoBack()}
        {renderProjectTitle()}
      </div>
      {userPackageInfo()}
    </StyledHeader>
  );
};

export default Header;
